import React, { useEffect, useState } from 'react';
import { Layout } from "@/components/Layout/Layout";
import { GetFilterUrl } from '@/utility/helper';
import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IOrderResponse, IOrder } from '@/interfaces/Order/order.interface';
import { IStore, IStoreListResponse } from '@/interfaces/Store/store.interface';
import { Range } from 'react-date-range';
import { BusinessKPISection } from './BusinessKPISection';
import { BusinessChartsSection } from './BusinessChartsSection';
import { IDashboardApiResponse, IBusinessKPIData } from '@/interfaces/Dashboard/dashboard.interface';
import { FilterBar } from '@/components/common/FilterBar';
import { BusinessTablesSection } from './BusinessTablesSection';
import { AdminStoreDashboardTab } from './AdminStoreDashboardTab';
import { LayoutDashboard, Store } from 'lucide-react';

export function AdminDashboardView() {
    const [activeTab, setActiveTab] = useState<'platform' | 'store'>('platform');
    const [activeFilter, setActiveFilter] = useState<string>('');
    const [customDateRange, setCustomDateRange] = useState<Range[]>([{
        startDate: new Date(), endDate: new Date(), key: 'selection'
    }]);
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');
    const [selectedStatus, setSelectedStatus] = useState<string>('');

    const [adminKpiData, setAdminKpiData] = useState<IBusinessKPIData | null>(null);
    const [adminKpiLoading, setAdminKpiLoading] = useState(true);
    const [adminOrders, setAdminOrders] = useState<IOrder[]>([]);
    const [storesList, setStoresList] = useState<IStore[]>([]);
    const [tablesLoading, setTablesLoading] = useState(true);

    const [adminChartsData, setAdminChartsData] = useState<{
        revenueData: { label: string; revenue: number; orders: number }[];
        storeData: { storeName: string; revenue: number; ordersToday: number }[];
        deliveryData: { name: string; value: number; color: string }[];
        funnelData: { stage: string; count: number }[];
    }>({
        revenueData: [],
        storeData: [],
        deliveryData: [],
        funnelData: [],
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setAdminKpiLoading(true);
                setTablesLoading(true);

                const orderParams = GetFilterUrl({
                    limit: 5,
                    page: 1,
                    ...(activeFilter === 'custom' && customDateRange[0].startDate && customDateRange[0].endDate ? {
                        startDate: customDateRange[0].startDate.toISOString().split('T')[0],
                        endDate: customDateRange[0].endDate.toISOString().split('T')[0],
                    } : {}),
                    sort: "newest",
                    ...(selectedStoreId ? { storeId: selectedStoreId } : {}),
                    ...(selectedStatus ? { status: selectedStatus } : {}),
                });

                const dashboardParams = GetFilterUrl({
                    ...(activeFilter && activeFilter !== 'custom' ? { filter: activeFilter } : {}),
                    ...(activeFilter === 'custom' && customDateRange[0].startDate && customDateRange[0].endDate ? {
                        filter: 'custom',
                        startDate: customDateRange[0].startDate.toISOString().split('T')[0],
                        endDate: customDateRange[0].endDate.toISOString().split('T')[0],
                    } : {}),
                    ...(selectedStoreId ? { storeId: selectedStoreId } : {}),
                    ...(selectedStatus ? { status: selectedStatus } : {}),
                });

                const ordersPromise = callApi<IOrderResponse>(
                    `${API_URL}${API_ENDPOINT.orders}?${orderParams}`, { method: 'GET' }
                );

                const adminDashboardPromise = callApi<IDashboardApiResponse>(
                    `${API_URL}${API_ENDPOINT.dashboard}?${dashboardParams}`, { method: 'GET' }
                );

                const [ordersResponse, adminResponse] = await Promise.all([
                    ordersPromise, adminDashboardPromise,
                ]);

                if (ordersResponse.data?.order) {
                    setAdminOrders(ordersResponse.data.order);
                }

                if (adminResponse?.data) {
                    const { criticalOperational: co, financial: fin, informational: info, charts } = adminResponse.data;

                    setAdminKpiData({
                        totalOrdersToday: co.ordersToday,
                        activeOrders: co.activeOrders,
                        pendingDeliveries: co.pendingDelivery,
                        delayedOrders: co.delayedOrders,
                        pendingPayout: co.pendingPayout,
                        driverRequest: co.driverRequest,
                        failedOrders: co.failedOrders,
                        activeStores: co.activeStores,
                        storesOffline: co.storesOffline,
                        driversAvailable: co.driversAvailable,
                        grossEarning: fin.grossEarning,
                        netEarning: fin.netEarning,
                        pendingCod: fin.pendingCod,
                        totalRefundedAmount: fin.totalRefundedAmount,
                        totalStorePaidAmount: fin.totalStorePaidAmount,
                        totalDriverPaidAmount: fin.totalDriverPaidAmount
                        // customerSatisfactionScore: info.customerSatisfaction,
                        // repeatCustomerPercent: info.repeatCustomers,
                        // peakActiveHour: info.peakActiveHour,
                    });

                    const revTrend = charts.revenueTrend || [];
                    const volTrend = charts.orderVolumeTrend || [];
                    const mergedRevenue = revTrend.map((r: any, i: number) => {
                        let label = '';
                        if (activeFilter === 'today' && r.hour !== undefined) {
                            const hr = parseInt(r.hour, 10);
                            if (!isNaN(hr)) {
                                const ampm = hr >= 12 ? 'PM' : 'AM';
                                const displayHr = hr % 12 === 0 ? 12 : hr % 12;
                                label = `${displayHr} ${ampm}`;
                            } else {
                                label = `${r.hour}:00`;
                            }
                        } else {
                            const dateStr = r.date || r.month;
                            const date = dateStr ? new Date(dateStr) : null;
                            if (date && !isNaN(date.getTime())) {
                                label = date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
                            } else {
                                label = r.hour ? `${r.hour}:00` : '';
                            }
                        }
                        return {
                            label,
                            revenue: r.earning || 0,
                            orders: volTrend[i]?.count || 0,
                        };
                    });

                    const dsr = charts.deliverySuccessRate;
                    const deliverySlices = [
                        { name: 'Delivered', value: dsr.deliveredPct, color: '#10B981' },
                        { name: 'In Transit', value: dsr.inTransitPct, color: '#3B82F6' },
                        { name: 'Cancelled', value: dsr.cancelledPct, color: '#F59E0B' },
                        // { name: 'Failed', value: dsr.failedPct, color: '#EF4444' },
                    ].filter(s => s.value > 0);

                    setAdminChartsData({
                        revenueData: mergedRevenue,
                        storeData: charts.topPerformingStores || [],
                        deliveryData: deliverySlices,
                        funnelData: charts.orderStatusFunnel || [],
                    });
                }
            } catch (error) {
                console.error("Admin Dashboard fetch error:", error);
            } finally {
                setAdminKpiLoading(false);
                setTablesLoading(false);
            }
        };
        fetchData();
    }, [activeFilter, customDateRange, selectedStoreId, selectedStatus]);

    const currentDate = new Date().toLocaleDateString('en-ZA', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    const mappedOrders = adminOrders.map(o => ({
        id: o.id,
        orderId: `#${o.orderId}`,
        store: o.items?.[0]?.product?.store?.name || '—',
        customer: o.user?.fullName || 'Guest',
        amount: Number(o.finalAmount || o.totalAmount || 0),
        paymentType: o.paymentMethod || '—',
        status: o.status,
        driver: o.driver?.fullName,
        isDelayed: o.status === 'delayed',
        createdAt: new Date(o.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    }));

    const mappedStores = storesList.map(storeData => {
        const perf = adminChartsData.storeData.find(s => s.storeName.toLowerCase() === storeData.name.toLowerCase());
        const ordersForStore = adminOrders.filter(o => o.items?.[0]?.product?.store?.id === storeData.id);
        const totalOrders = ordersForStore.length;
        const cancelledOrders = ordersForStore.filter(o => o.status === 'cancelled').length;
        const cancellationRate = totalOrders > 0 ? Math.round((cancelledOrders / totalOrders) * 100) : 0;

        return {
            id: storeData.id,
            storeName: storeData.name,
            ordersToday: perf?.ordersToday || totalOrders || 0,
            revenue: perf?.revenue || ordersForStore.reduce((sum, o) => sum + Number(o.finalAmount), 0) || 0,
            avgPrepTime: 15,
            cancellationRate: cancellationRate,
            isOnline: storeData.status === 'online',
            rating: 4.5
        };
    });

    const mappedDeliveryIssues = adminOrders
        .filter(o => o.status === 'delayed' || (o.status === 'out_for_delivery' && (new Date().getTime() - new Date(o.createdAt).getTime()) > 45 * 60 * 1000))
        .map(o => {
            const diffMs = new Date().getTime() - new Date(o.createdAt).getTime();
            const diffMins = Math.floor(diffMs / 60000);
            const delayMins = diffMins > 30 ? diffMins - 30 : 15;
            return {
                id: o.id,
                orderId: `#${o.orderId}`,
                driver: o.driver?.fullName || 'Unassigned',
                delayDuration: `${delayMins} min late`,
                issueType: o.status === 'accepted' || o.status === 'in_progress' ? 'Store Prep Delay' : 'Traffic Delay',
                escalationStatus: delayMins > 30 ? 'Escalated' : 'Pending'
            };
        });

    const mappedFailedOrders = adminOrders
        .filter(o => o.status === 'cancelled' || o.status === 'failed' || o.status === 'refund_requested' || o.status === 'refunded')
        .map(o => {
            let refundStatus: 'Pending' | 'Processed' | 'None' = 'None';
            if (o.status === 'refunded') refundStatus = 'Processed';
            else if (o.status === 'refund_requested' || o.status === 'refund_in_progress') refundStatus = 'Pending';

            return {
                id: o.id,
                orderId: `#${o.orderId}`,
                store: o.items?.[0]?.product?.store?.name || '—',
                failureReason: o.deliveryInstructions?.includes('cancelled') ? 'Customer Cancelled' : (o.status === 'failed' ? 'Payment Failed' : 'Cancelled by Store'),
                refundStatus,
                hasComplaint: o.status === 'refund_requested'
            };
        });

    return (
        <Layout>
            <div className="space-y-6 pb-20 animate-in fade-in duration-500">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
                        <p className="text-sm text-muted-foreground">Yebo Eggs · {currentDate}</p>
                    </div>
                </div>

                {/* Tab Switcher */}
                <div className="flex gap-1 border-b border-border">
                    <button
                        onClick={() => setActiveTab('platform')}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all rounded-t-md ${activeTab === 'platform' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/40 cursor-pointer'}`}
                    >
                        <LayoutDashboard className="w-4 h-4" /> Platform Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('store')}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-all rounded-t-md ${activeTab === 'store' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/40 cursor-pointer'}`}
                    >
                        <Store className="w-4 h-4" /> Store Overview
                    </button>
                </div>

                {activeTab === 'platform' ? (
                    <div className="space-y-6 animate-in fade-in duration-300">
                        <FilterBar
                            activeFilter={activeFilter}
                            setActiveFilter={setActiveFilter}
                            customDateRange={customDateRange}
                            setCustomDateRange={setCustomDateRange}
                            selectedStoreId={selectedStoreId}
                            setSelectedStoreId={setSelectedStoreId}
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                            stores={storesList}
                        />

                        <BusinessKPISection data={adminKpiData} loading={adminKpiLoading} activeFilter={activeFilter} />

                        <BusinessChartsSection
                            revenueData={adminChartsData.revenueData}
                            storeData={adminChartsData.storeData}
                            deliveryData={adminChartsData.deliveryData}
                            funnelData={adminChartsData.funnelData}
                            loading={adminKpiLoading}
                        />

                        <BusinessTablesSection
                            orders={mappedOrders}
                            stores={mappedStores}
                            deliveryIssues={mappedDeliveryIssues}
                            failedOrders={mappedFailedOrders}
                            loading={tablesLoading}
                            activeFilter={activeFilter}
                        />
                    </div>
                ) : (
                    <AdminStoreDashboardTab />
                )}
            </div>
        </Layout>
    );
}
